/**
 * 將後端回傳的預約時段正規化為 { time, isFull }。
 *
 * 相容新舊兩種格式：舊版直接回時間字串，新版回 { time, isFull } 物件。
 * 時間截為 HH:mm（後端帶秒，picker 只顯示到分）；舊格式與缺值一律視為未額滿。
 *
 * @param {string | { time: string, isFull?: boolean }} raw 後端時段資料
 * @returns {{ time: string, isFull: boolean } | null} 格式無效時回 null，由呼叫端 filter 掉
 */
export const normalizeBookingTime = raw => {
	if (typeof raw === 'string') return { time: raw.slice(0, 5), isFull: false }
	const time = raw?.time
	if (typeof time !== 'string') return null
	return { time: time.slice(0, 5), isFull: !!raw.isFull }
}

/**
 * 取出指定日期中尚未過期的可預約時段，供 picker 直接當 data 使用。
 *
 * 門檻以當下時間的整分起算再加上最小預約間隔，時段 >= 門檻即保留，
 * 故整點時段仍屬於該分鐘、不會被自己排除。額滿時段不過濾，改標 disabled。
 *
 * @param {Array<{ date: string, timeList: Array }>} [bookingList] 店家預約時段設定
 * @param {string} date 目標日期（YYYY-MM-DD）
 * @param {number} [minInterval=0] 最小預約間隔（分鐘）
 * @returns {Array<Array<Object>>} picker 的二維欄位資料（單欄）。無可選時段時回
 *   哨兵，哨兵標為 disabled 讓 picker emit null，避免被當成選取結果寫回取餐時間
 */
export const getBookableTimeSlots = (bookingList, date, minInterval = 0) => {
	const found = bookingList?.find(d => d.date === date)
	if (!found) return [[{ label: '無可選項目', disabled: true }]]

	const now = new Date()
	now.setSeconds(0, 0)
	const threshold = now.getTime() + minInterval * 60 * 1000
	const slots = (found.timeList ?? [])
		.map(normalizeBookingTime)
		.filter(Boolean)
		.map(({ time, isFull }) => ({
			label: isFull ? `${time} (額滿)` : time,
			value: time,
			disabled: isFull,
			timestamp: new Date(`${date}T${time}:00`).getTime(),
		}))
		.filter(slot => slot.timestamp >= threshold)

	return slots.length ? [slots] : [[{ label: '無可選項目', disabled: true }]]
}

/**
 * 指定時間是否仍在時段清單中且可選；額滿與哨兵皆視為不可選。
 *
 * @param {Array<Array<Object>>} options getBookableTimeSlots 的回傳值
 * @param {string} value 要檢查的時間（HH:mm）
 * @returns {boolean}
 */
export const isTimeSlotSelectable = (options, value) =>
	!!options?.[0]?.some(item => item.value === value && !item.disabled)

/**
 * 已選的預約時段是否仍可預約。
 *
 * 取餐表單是彈窗，關閉後就沒有 watch 在盯著時段清單，店家中途調整預約設定
 * 或時段額滿時，先前選好的時間會靜靜留在狀態裡；送單前必須以最新設定再驗一次。
 *
 * @param {Array<{ date: string, timeList: Array }>} [bookingList] 店家預約時段設定
 * @param {string} date 已選日期（YYYY-MM-DD）
 * @param {string} time 已選時間（HH:mm）
 * @param {number} [minInterval=0] 最小預約間隔（分鐘）
 * @returns {boolean} 日期或時間缺值時回 false（沒有可送出的預約時段）
 */
export const isBookedSlotStillAvailable = (
	bookingList,
	date,
	time,
	minInterval = 0,
) => {
	if (!date || !time) return false
	return isTimeSlotSelectable(
		getBookableTimeSlots(bookingList, date, minInterval),
		time,
	)
}
